import { useState, useEffect } from "react";
import { supabase } from "./supabase";
import { T, applyTheme } from "./theme/index";
import { INIT_CATS, INIT_INV, MONTHS_SHORT, MONTHS_FULL, NAV, PAGE_TITLE } from "./constants/index";
import { useIsMobile, Toast, DashboardSkeleton } from "./components/ui/index";
import Dashboard  from "./components/pages/Dashboard";
import Dompet     from "./components/pages/Dompet";
import Transaksi  from "./components/pages/Transaksi";
import Anggaran   from "./components/pages/Anggaran";
import Investasi  from "./components/pages/Investasi";
import Milestone  from "./components/pages/Milestone";
import Laporan    from "./components/pages/Laporan";
import Kategori   from "./components/pages/Kategori";
import Backup     from "./components/pages/Backup";
import Login      from "./auth/Login";
import TxModal    from "./components/modals/TxModal";



export default function App() {
  const now = new Date();
  const isMobile = useIsMobile();
  const [page, setPage]   = useState("dashboard");
  const [month, setMonth] = useState(now.getMonth());
  const [year, setYear]   = useState(now.getFullYear());
  const [txModal, setTxModal] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isDark,   setIsDark]   = useState(true);

  const toggleTheme = () => {
    const next = isDark ? "light" : "dark";
    applyTheme(next);
    setIsDark(!isDark);
  };

  const [transactions, setTransactions] = useState([]);
  const [categories,   setCategories]   = useState(INIT_CATS);
  const [investments,  setInvestments]  = useState(INIT_INV);
  const [investmentSales, setInvestmentSales] = useState([]);
  const [budgets,      setBudgets]      = useState([]);
  const [wallets,      setWallets]      = useState([]);
  const [milestones,   setMilestones]   = useState([]);
  const [loaded,       setLoaded]       = useState(false);
  const [hadRowOnLoad, setHadRowOnLoad] = useState(null); // null=belum tau, true=baris app_data sudah ada, false=user baru (belum pernah save)
  const [session,      setSession]      = useState(undefined); // undefined=checking, null=logged out
  const [saving,       setSaving]       = useState(false);
  const [toast,        setToast]        = useState(null); // { message, type }

  // Font smoothing + tabular nums global
  useEffect(() => {
    document.documentElement.style.setProperty("-webkit-font-smoothing", "antialiased");
    document.documentElement.style.setProperty("-moz-osx-font-smoothing", "grayscale");
  }, []);

  // Theme init dari localStorage
  useEffect(() => {
    const saved = (() => { try { return localStorage.getItem("jf_theme") || "dark"; } catch { return "dark"; }})();
    applyTheme(saved);
    setIsDark(saved === "dark");
  }, []);

  // ── Auth session ──
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session ?? null));
    const { data: sub } = supabase.auth.onAuthStateChange((_event, sess) => {
      setSession(sess ?? null);
      if (!sess) { setLoaded(false); setHadRowOnLoad(null); } // reset saat logout
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  // ── Load data dari Supabase saat login ──
  useEffect(() => {
    if (!session) return;
    // Reset state sebelum load akun baru — cegah data akun lama tampil
    setTransactions([]);
    setCategories(INIT_CATS);
    setInvestments(INIT_INV);
    setInvestmentSales([]);
    setBudgets([]);
    setWallets([]);
    setLoaded(false);
    setHadRowOnLoad(null);
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("app_data")
          .select("data")
          .eq("user_id", session.user.id)
          .maybeSingle();
        if (cancelled) return;

        if (error) {
          console.error("LOAD ERROR:", error);
          setToast({ message: "Gagal memuat data dari cloud. Coba refresh halaman.", type: "error" });
          return; // loaded tetap false
        }

        if (data?.data) {
          const d = data.data;
          if (d.transactions) setTransactions(d.transactions);
          if (d.categories)   setCategories(d.categories);
          if (d.investments)  setInvestments(d.investments);
          if (d.investmentSales) setInvestmentSales(d.investmentSales);
          if (d.budgets)      setBudgets(d.budgets);
          if (d.wallets)      setWallets(d.wallets);
          if (d.milestones)   setMilestones(d.milestones);
        }
        setHadRowOnLoad(data !== null);
        setLoaded(true);
      } catch (err) {
        if (cancelled) return;
        console.error("LOAD EXCEPTION:", err);
        setToast({ message: "Gagal memuat data dari cloud. Coba refresh halaman.", type: "error" });
        // loaded tetap false
      }
    })();
    return () => { cancelled = true; };
  }, [session]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Save data ke Supabase (debounced) ──
  useEffect(() => {
    if (!loaded || !session) return;
    // Safety net: cegah save kosong menimpa data yang sudah ada, kecuali memang user baru
    const allEmpty = transactions.length === 0 && wallets.length === 0 && budgets.length === 0 && investments.length === 0;
    if (allEmpty && hadRowOnLoad !== false) return;
    setSaving(true);
    const userId = session.user.id; // capture sekarang
    const timer = setTimeout(async () => {
      // Safety: verifikasi session masih sama sebelum save
      // mencegah data akun lama tersimpan ke akun baru
      const { data: curr } = await supabase.auth.getSession();
      if (curr?.session?.user?.id !== userId) {
        setSaving(false);
        return;
      }
      const { error } = await supabase.from("app_data").upsert({
        user_id: userId,
        data: { transactions, categories, investments, investmentSales, budgets, wallets, milestones },
        updated_at: new Date().toISOString(),
      }, { onConflict: "user_id" });
      if (error) {
        console.error("SUPABASE SAVE ERROR:", error);
        setToast({ message: "Gagal menyimpan ke cloud: " + error.message, type: "error" });
      }
      setSaving(false);
    }, 800);
    return () => clearTimeout(timer);
  }, [transactions, categories, investments, investmentSales, budgets, wallets, milestones, loaded, session, hadRowOnLoad]); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = () => supabase.auth.signOut();

  const addTx    = (tx)  => setTransactions(p=>[tx,...p]);
  const editTx   = (tx)  => setTransactions(p=>p.map(t=>t.id===tx.id?tx:t));
  const deleteTx = (id)  => setTransactions(p=>p.filter(t=>t.id!==id));
  const addCat   = (cat) => setCategories(p=>[...p,cat]);
  const editCat  = (cat) => setCategories(p=>p.map(c=>c.id===cat.id?cat:c));
  const deleteCat= (id)  => setCategories(p=>p.filter(c=>c.id!==id));
  const addInv   = (inv) => setInvestments(p=>[...p,inv]);
  const editInv  = (inv) => setInvestments(p=>p.map(i=>i.id===inv.id?inv:i));
  const deleteInv= (id)  => setInvestments(p=>p.filter(i=>i.id!==id));
  const sellInv  = (sale, updatedInv) => {
    setInvestmentSales(p=>[...p, sale]);
    setInvestments(p=>p.map(i=>i.id===updatedInv.id?updatedInv:i));
  };

  const addMilestone    = (m) => setMilestones(p=>[...p,m]);
  const editMilestone   = (m) => setMilestones(p=>p.map(x=>x.id===m.id?m:x));
  const deleteMilestone = (id) => setMilestones(p=>p.filter(x=>x.id!==id));
  const addWallet    = (w) => setWallets(p=>[...p,w]);
  const editWallet   = (w) => setWallets(p=>p.map(x=>x.id===w.id?w:x));
  const deleteWallet = (id) => setWallets(p=>p.filter(x=>x.id!==id));
  const saveBudget = (nb) => setBudgets(prev=>[...prev.filter(b=>!(b.month===month&&b.year===year)),...nb]);
  const prevMonth  = () => { if(month===0){setMonth(11);setYear(y=>y-1);}else setMonth(m=>m-1); };
  const nextMonth  = () => { if(month===11){setMonth(0);setYear(y=>y+1);}else setMonth(m=>m+1); };

  // ── Export / Import ──
  const exportData = () => {
    const payload = {
      app: "JF Finance",
      version: 1,
      exportedAt: new Date().toISOString(),
      data: { transactions, categories, investments, investmentSales, budgets, wallets, milestones },
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    const stamp = new Date().toISOString().slice(0,10);
    a.href = url;
    a.download = `japa-backup-${stamp}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importData = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target.result);
        const d = parsed.data || parsed;
        if (!d.transactions && !d.wallets && !d.investments) {
          setToast({ message: "File tidak dikenali. Pastikan ini file backup JF Finance.", type: "error" });
          return;
        }
        const counts = `${d.transactions?.length||0} transaksi, ${d.wallets?.length||0} rekening, ${d.investments?.length||0} investasi`;
        if (!window.confirm(`Import akan MENGGANTI semua data saat ini dengan:\n${counts}\n\nLanjutkan?`)) return;
        if (d.transactions) setTransactions(d.transactions);
        if (d.categories)   setCategories(d.categories);
        if (d.investments)  setInvestments(d.investments);
        if (d.investmentSales) setInvestmentSales(d.investmentSales);
        if (d.budgets)      setBudgets(d.budgets);
        if (d.wallets)      setWallets(d.wallets);
        if (d.milestones)   setMilestones(d.milestones);
        setToast({ message: "Data berhasil diimport.", type: "success" });
      } catch {
        setToast({ message: "Gagal membaca file. Pastikan format JSON valid.", type: "error" });
      }
    };
    reader.readAsText(file);
    e.target.value = ""; // reset agar bisa import file sama lagi
  };

  const showMonthNav = ["dashboard","dompet","transaksi","anggaran"].includes(page);

  // Cek session masih berjalan
  if (session === undefined) return (
    <div style={{ background:T.bg, height:"100vh", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ color:T.muted, fontSize:15, display:"flex", alignItems:"center", gap:10 }}>
        <div style={{ width:10, height:10, borderRadius:"50%", background:T.gold }} />
        Memuat...
      </div>
    </div>
  );

  // Belum login → tampilkan halaman login
  if (session === null) return <Login />;

  if (!loaded) return (
    <div style={{ background:T.bg, minHeight:"100vh", padding:16 }}>
      <DashboardSkeleton />
    </div>
  );

  return (
    <div style={{ display:"flex", flexDirection:isMobile?"column":"row", height:"100vh", background:"var(--c-bg)", fontFamily:"'DM Sans',sans-serif", overflow:"hidden" }}>

      {/* Desktop Sidebar */}
      {!isMobile && (
        <div style={{ width:210, background:T.card, borderRight:`1px solid ${T.border}`, display:"flex", flexDirection:"column", flexShrink:0 }}>
          <div style={{ padding:"20px 18px 16px", borderBottom:`1px solid ${T.border}` }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <img src="/icon-192.png" alt="JF" style={{ width:38, height:38, borderRadius:10, objectFit:"cover" }} />
              <div>
                <div style={{ color:T.text, fontWeight:800, fontSize:16, letterSpacing:"-0.025em", lineHeight:1 }}>JF Finance</div>
                <div style={{ color:T.muted, fontSize:10, letterSpacing:"0.12em", fontWeight:500 }}>FINANCIAL SOLUTIONS</div>
              </div>
            </div>
          </div>
          <nav style={{ flex:1, padding:"10px 8px" }}>
            {NAV.map(item => (
              <button key={item.id} onClick={() => setPage(item.id)} style={{ width:"100%", display:"flex", alignItems:"center", gap:9, padding:"9px 11px", borderRadius:9, marginBottom:1, cursor:"pointer", border:"none", textAlign:"left", background:page===item.id?T.gold+"18":"transparent", color:page===item.id?T.gold:T.muted, fontWeight:page===item.id?700:400, fontSize:14, borderLeft:page===item.id?`3px solid ${T.gold}`:`3px solid transparent`, transition:"background 0.15s, color 0.15s" }}>
                <span style={{ fontSize:16 }}>{item.icon}</span>
                {item.label}
                {page===item.id && <div style={{ marginLeft:"auto", width:5, height:5, borderRadius:"50%", background:T.gold }} />}
              </button>
            ))}
          </nav>
          <div style={{ padding:"12px 18px", borderTop:`1px solid ${T.border}` }}>
            <div style={{ color:T.text, fontSize:11, fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{session.user.email}</div>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:6, gap:6 }}>
              <span style={{ color:saving?T.gold:T.muted, fontSize:10, display:"flex", alignItems:"center", gap:4 }}>
                <span style={{ width:6, height:6, borderRadius:"50%", background:saving?T.gold:T.green }} />
                {saving ? "Menyimpan..." : "Tersimpan"}
              </span>
              <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                <button onClick={toggleTheme} title={isDark?"Light mode":"Dark mode"} style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:6, cursor:"pointer", padding:"4px 8px", fontSize:14, WebkitTapHighlightColor:"transparent" }}>
                  {isDark ? "☀️" : "🌙"}
                </button>
                <button onClick={logout} style={{ background:"none", border:"none", color:T.muted, fontSize:11, cursor:"pointer", textDecoration:"underline" }}>Keluar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden", minWidth:0 }}>

        {/* Top bar */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:isMobile?"12px 16px":"12px 22px", paddingTop:isMobile?"max(12px, calc(env(safe-area-inset-top) + 8px))":"12px", borderBottom:`1px solid ${T.border}`, background:T.card, flexShrink:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            {isMobile && (
              <button onClick={() => setMenuOpen(true)} style={{ background:"none", border:"none", cursor:"pointer", padding:"4px 6px 4px 0", display:"flex", flexDirection:"column", gap:4, WebkitTapHighlightColor:"transparent" }}>
                {[0,1,2].map(i => <span key={i} style={{ width:20, height:2, borderRadius:2, background:T.text, display:"block" }} />)}
              </button>
            )}
            {isMobile && (
              <img src="/icon-192.png" alt="JF" style={{ width:28, height:28, borderRadius:7, objectFit:"cover" }} />
            )}
            <div style={{ color:T.text, fontWeight:800, fontSize:isMobile?16:20, letterSpacing:"-0.02em" }}>{PAGE_TITLE[page]}</div>
          </div>
          {showMonthNav && (
            <div style={{ display:"flex", alignItems:"center", gap:4, background:T.surface, borderRadius:8, padding:"4px 6px", border:`1px solid ${T.border}` }}>
              <button onClick={prevMonth} style={{ background:"none", border:"none", color:T.muted, cursor:"pointer", fontSize:14, padding:"0 6px", lineHeight:1, WebkitTapHighlightColor:"transparent" }}>◀</button>
              <span style={{ color:T.text, fontWeight:600, fontSize:isMobile?11:13, minWidth:isMobile?100:140, textAlign:"center" }}>{isMobile?MONTHS_SHORT[month]:MONTHS_FULL[month]} {year}</span>
              <button onClick={nextMonth} style={{ background:"none", border:"none", color:T.muted, cursor:"pointer", fontSize:14, padding:"0 6px", lineHeight:1, WebkitTapHighlightColor:"transparent" }}>▶</button>
            </div>
          )}
        </div>

        {/* Page content */}
        <div style={{ flex:1, overflow:"auto", padding:isMobile?"12px 14px 24px":"20px", WebkitOverflowScrolling:"touch" }}>
          {page==="dashboard" && <Dashboard transactions={transactions} categories={categories} investments={investments} wallets={wallets} month={month} year={year} onAddTx={() => setTxModal("add")} />}
          {page==="dompet"    && <Dompet wallets={wallets} transactions={transactions} month={month} year={year} onAdd={addWallet} onEdit={editWallet} onDelete={deleteWallet} />}
          {page==="transaksi" && <Transaksi transactions={transactions} categories={categories} wallets={wallets} month={month} year={year} onAdd={addTx} onEdit={editTx} onDelete={deleteTx} openModal={txModal} setOpenModal={setTxModal} />}
          {page==="anggaran"  && <Anggaran  transactions={transactions} categories={categories} wallets={wallets} budgets={budgets} month={month} year={year} onSaveBudget={saveBudget} />}
          {page==="investasi" && <Investasi investments={investments} investmentSales={investmentSales} onAdd={addInv} onEdit={editInv} onDelete={deleteInv} onBulkEdit={setInvestments} onSell={sellInv} />}
          {page==="milestone" && <Milestone investments={investments} milestones={milestones} onAdd={addMilestone} onEdit={editMilestone} onDelete={deleteMilestone} />}
          {page==="laporan"   && <Laporan   transactions={transactions} categories={categories} investments={investments} budgets={budgets} />}
          {page==="kategori"  && <Kategori  categories={categories} onAdd={addCat} onEdit={editCat} onDelete={deleteCat} />}
          {page==="backup"    && <Backup transactions={transactions} wallets={wallets} investments={investments} investmentSales={investmentSales} budgets={budgets} onExport={exportData} onImport={importData} userEmail={session.user.email} onLogout={logout} saving={saving} />}
        </div>
      </div>

      {/* Mobile Drawer Menu */}
      {isMobile && menuOpen && (
        <div style={{ position:"fixed", inset:0, zIndex:1500, display:"flex" }}>
          {/* Backdrop */}
          <div onClick={() => setMenuOpen(false)} style={{ position:"absolute", inset:0, background:"rgba(4,7,18,0.7)", backdropFilter:"blur(2px)" }} />
          {/* Drawer panel */}
          <div style={{ position:"relative", width:"min(78vw,280px)", height:"100%", background:T.card, borderRight:`1px solid ${T.border}`, display:"flex", flexDirection:"column", animation:"none" }}>
            <div style={{ padding:"20px 18px 16px", paddingTop:"max(20px, calc(env(safe-area-inset-top) + 14px))", borderBottom:`1px solid ${T.border}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <img src="/icon-192.png" alt="JF" style={{ width:38, height:38, borderRadius:10, objectFit:"cover" }} />
                <div>
                  <div style={{ color:T.text, fontWeight:800, fontSize:16, letterSpacing:"-0.025em", lineHeight:1 }}>JF Finance</div>
                  <div style={{ color:T.muted, fontSize:10, letterSpacing:"0.12em", fontWeight:500 }}>FINANCIAL SOLUTIONS</div>
                </div>
              </div>
              <button onClick={() => setMenuOpen(false)} style={{ background:"none", border:"none", color:T.muted, cursor:"pointer", fontSize:24, lineHeight:1, padding:"0 4px", WebkitTapHighlightColor:"transparent" }}>×</button>
            </div>
            <nav style={{ flex:1, padding:"10px 8px", overflow:"auto" }}>
              {NAV.map(item => (
                <button key={item.id} onClick={() => { setPage(item.id); setMenuOpen(false); }} style={{ width:"100%", display:"flex", alignItems:"center", gap:11, padding:"12px 13px", borderRadius:9, marginBottom:2, cursor:"pointer", border:"none", textAlign:"left", background:page===item.id?T.gold+"18":"transparent", color:page===item.id?T.gold:T.muted, fontWeight:page===item.id?700:400, fontSize:14, borderLeft:page===item.id?`3px solid ${T.gold}`:`3px solid transparent`, transition:"background 0.15s, color 0.15s", WebkitTapHighlightColor:"transparent" }}>
                  <span style={{ fontSize:17, width:24, textAlign:"center", flexShrink:0 }}>{item.icon}</span>
                  {item.label}
                </button>
              ))}
            </nav>
            <div style={{ padding:"14px 18px", borderTop:`1px solid ${T.border}` }}>
              <div style={{ color:T.text, fontSize:12, fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{session.user.email}</div>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:8 }}>
                <span style={{ color:saving?T.gold:T.muted, fontSize:11, display:"flex", alignItems:"center", gap:5 }}>
                  <span style={{ width:6, height:6, borderRadius:"50%", background:saving?T.gold:T.green }} />
                  {saving ? "Menyimpan..." : "Tersimpan"}
                </span>
                <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                  <button onClick={toggleTheme} style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:6, cursor:"pointer", padding:"5px 10px", fontSize:15, WebkitTapHighlightColor:"transparent" }}>
                    {isDark ? "☀️" : "🌙"}
                  </button>
                  <button onClick={logout} style={{ background:"none", border:"none", color:T.red, fontSize:12, cursor:"pointer", fontWeight:600, WebkitTapHighlightColor:"transparent" }}>Keluar</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Global TX modal */}
      {txModal==="add" && page==="dashboard" && (
        <TxModal tx={null} categories={categories} wallets={wallets} onSave={addTx} onClose={() => setTxModal(null)} />
      )}

      <Toast message={toast?.message} type={toast?.type} onDismiss={() => setToast(null)} />
    </div>
  );
}
